import { z } from "zod";
import { twinProductQueryCreateInputSchema, twinProductQueryCreateResponseSchema, twinProductQueryGetResponseSchema, } from "./twin-queries.js";
const idSchema = z.string().trim().min(1).max(200);
const slugSchema = z.string().trim().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/u);
const timestampSchema = z.number().int().nonnegative();
export const twinProductHealthStatusSchema = z.enum([
    "healthy",
    "degraded",
    "failing",
    "untested",
]);
const twinProductHealthCountsSchema = z.object({
    healthy: z.number().int().nonnegative(),
    degraded: z.number().int().nonnegative(),
    failing: z.number().int().nonnegative(),
    untested: z.number().int().nonnegative(),
}).strict();
export const twinProductTestCaseSchema = z.object({
    suiteId: slugSchema,
    suiteVersion: z.number().int().positive(),
    caseId: slugSchema,
    label: z.string().trim().min(1).max(240),
    question: z.string().trim().min(1).max(8_000),
}).strict();
export const twinProductHealthDetailsSchema = z.object({
    title: z.string().trim().min(1).max(200),
    description: z.string().trim().min(1).max(2_000),
    canonicalDefinition: z.string().trim().min(1).max(8_000),
    grain: z.string().trim().min(1).max(1_000),
    intents: z.array(z.string().trim().min(1).max(240)).max(64),
    unsupportedIntents: z.array(z.string().trim().min(1).max(240)).max(64),
    dimensions: z.array(z.string().trim().min(1).max(63)).max(80),
    measures: z.array(z.string().trim().min(1).max(63)).max(80),
    limitations: z.array(z.string().trim().min(1).max(1_000)).max(64),
    sourceRefs: z.array(z.string().trim().min(1).max(240)).max(80),
    sourceSystems: z.array(z.string().trim().min(1).max(120)).max(50),
    skills: z.array(z.object({
        slug: slugSchema,
        versionId: idSchema,
        markdown: z.string().min(1),
    }).strict()).min(1).max(64),
    implementation: z.object({
        relation: z.string().trim().min(1).max(260),
        sql: z.string().trim().min(1).max(2_000_000),
    }).strict(),
}).strict();
export const twinProductHealthItemSchema = z.object({
    publicationId: idSchema,
    publicationVersionId: idSchema,
    slug: slugSchema,
    version: z.number().int().positive(),
    relation: z.string().trim().min(1).max(260),
    certificationStatus: twinProductHealthStatusSchema,
    runtimeStatus: twinProductHealthStatusSchema,
    runs24h: z.number().int().nonnegative(),
    failures24h: z.number().int().nonnegative(),
    degradedRuns24h: z.number().int().nonnegative(),
    medianLatencyMs24h: z.number().int().nonnegative().nullable(),
    lastUsedAt: timestampSchema.nullable(),
    lastEvaluation: z.object({
        verdict: z.enum(["passed", "failed", "inconclusive"]),
        createdAt: timestampSchema,
        summary: z.string().trim().min(1).max(8_000),
    }).strict().nullable(),
    lastCertificationIncident: z.object({
        severity: z.enum(["warning", "error"]),
        category: z.string().trim().min(1).max(120),
        summary: z.string().trim().min(1).max(2_000),
        runId: idSchema.nullable(),
        createdAt: timestampSchema,
    }).strict().nullable(),
    lastRuntimeIncident: z.object({
        severity: z.enum(["warning", "error"]),
        category: z.string().trim().min(1).max(120),
        summary: z.string().trim().min(1).max(2_000),
        runId: idSchema.nullable(),
        createdAt: timestampSchema,
    }).strict().nullable(),
    testCases: z.array(twinProductTestCaseSchema).max(50),
    // Null is reserved for the explicit app/control-plane mixed-version window.
    details: twinProductHealthDetailsSchema.nullable(),
}).strict();
export const twinProductHealthResponseSchema = z.object({
    generatedAt: timestampSchema,
    summary: z.object({
        products: z.number().int().nonnegative(),
        certification: twinProductHealthCountsSchema,
        runtime: twinProductHealthCountsSchema,
    }).strict(),
    products: z.array(twinProductHealthItemSchema),
}).strict();
export const twinProductTestCreateInputSchema = twinProductQueryCreateInputSchema;
export const twinProductTestCreateResponseSchema = twinProductQueryCreateResponseSchema;
export const twinProductTestGetResponseSchema = twinProductQueryGetResponseSchema;
